import { t } from '@/utils/i18n'
import { useState, useEffect, useRef } from 'react'
import { useMount, useUpdateEffect } from 'ahooks'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Tabbar from '@/components/Tabbar/Tabbar'
import SearchBar from '@/components/SearchBar/Index'
import SortModal from '@/components/SortModal/SortModal'
import FilterModal from '@/components/FilterModal/FilterModal'
import ProductList from '@/components/ProductList/ProductList'
import { LeftOutline } from 'antd-mobile-icons'
import { utils, uploadEt, productUtil } from '@/utils'
import styles from "./CategorySearch.module.less";
import { useCorpSettingStore } from '@/stores'

import type {
  SortItem,
  RequestParams,
  FilterOption,
} from '@/types/product'

// 导入图片
import iconSort from '@/assets//img/icon/sort.svg'
import iconFilter from '@/assets/img/icon/filter.svg'
// import iconModeBig from "@/assets/img/icon/mode-big.svg";
// import iconModeList from "@/assets/img/icon/mode-list.svg";

const { cloneDeep, px2rem } = utils

function CategorySearch() {
  const { corpSetting } = useCorpSettingStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [listMode, setListMode] = useState(true) // 列表模式
  const [showSortModal, setShowSortModal] = useState(false) // 排序弹窗
  const [showFilterModal, setShowFilterModal] = useState(false) // 刷选弹窗
  const [minPrice, setMinPrice] = useState('') // 最低价格
  const [maxPrice, setMaxPrice] = useState('') // 最高价格
  const [deliveryType, setDeliveryType] = useState<number[]>([])
  const [sortIndex, setSortIndex] = useState(-1) // 当前排序索引
  const [sortKey, setSortKey] = useState('') // 当前排序key
  const [sortDirection, setSortDirection] = useState('DESC') // 当前排序方向
  const productHd = useRef<HTMLDivElement>(null)
  const [productHdHeight, setProductHdHeight] = useState(0)
  const [searchText, setSearchText] = useState('')

  // 商品列表搜索分类/推荐相关参数
  const [filter, setFilter] = useState<RequestParams>({
    otherType: 0, // 买过 传1
    productStatus: 0, // 推荐传2
    recommendType: 0, // 推荐时传 推荐类型：1新品 2爆品 4促销 99多货品分享
    categoryId: '0',
    categoryLevel: 0,
    combineType: 0,
    customerId: '0',
    interfaceVersion: 1,
    onScan: false,
    sortDirection: '1',
    sortKey: '5',
    unionMall: 0,
  })

  const onSearch = (text: string) => {
    setSearchText(text)
  }

  const handleScanSearch = () => {
    // 唤起 app 扫码
    if (window.webview && window.webview.scan) {
      window.webview.scan()
    }
  }
  // 获取 App 扫码结果
  const getScanValue = (text: string) => {
    setSearchText(text)
  }

  window.getScanValue = getScanValue

  // 切换排序
  function toggleShowSortModal() {
    hideAllModal('showSortModal')
    setShowSortModal(!showSortModal)
  }

  // 切换刷选
  function toggleShowFilterModal() {
    hideAllModal('showFilterModal')
    setShowFilterModal(!showFilterModal)
  }

  // 货品列表视图切换
  function handleChangeViewMode() {
    setListMode(!listMode)
  }
  console.log(handleChangeViewMode)

  // 排序筛选改变
  function handleChangeSort(sortItem: SortItem, index: number) {
    setSortIndex(index)
    setSortKey(sortItem.key)
    setSortDirection(sortItem.sortDirection || 'DESC')
    toggleShowSortModal()
  }

  // 价格过滤改变
  function handlePriceFilterChange(option: FilterOption) {
    setMinPrice(option.minPrice)
    setMaxPrice(option.maxPrice)
    setDeliveryType(option.deliveryType)
    toggleShowFilterModal()
  }

  function hideAllModal(key?: string) {
    switch (key) {
      case 'showSortModal':
        setShowFilterModal(false)
        break
      case 'showFilterModal':
        setShowSortModal(false)
        break
      default:
        setShowSortModal(false)
        setShowFilterModal(false)
        break
    }
  }

  // 初始化
  async function init() {
    const sortOptions = productUtil.initSortOptions(
      corpSetting.cloudProductSort,
      corpSetting.salesOn,
      corpSetting.salesSlipOn,
    ) // 初始化排序选项
  
    setSortIndex(sortOptions.sortActive.activeIndex as number)
    setSortKey(sortOptions.sortActive.activeKey)
    setSortDirection(sortOptions.sortActive.sortDirection as string)
  }

  useMount(async () => {
    uploadEt({ event_type: 3 }) // 埋点，首页打开货品分类结果页次数
    await init()
  })

  useEffect(() => {
    // document.title = t('全部货品')
    if (productHd.current) {
      setProductHdHeight(productHd.current.offsetHeight)
    }
  }, [])

  // 设置请求参数
  useUpdateEffect(() => {
    filter.combineType = 0
    filter.sortKey = sortKey
    filter.sortDirection = sortDirection === 'ASC' ? '0' : '1'
    filter.dataValue = searchParams.get('dataValue')
    filter.dataType = searchParams.get('dataType')
    filter.sort = searchParams.get('sort')
    filter.searchText = searchText

    if (deliveryType.length) {
      if (deliveryType.length === 1) {
        filter.deliveryType = deliveryType[0]
      } else {
        delete filter.deliveryType
      }
    } else {
      delete filter.deliveryType
    }

    if (minPrice) {
      filter.minPrice = minPrice
    } else {
      delete filter.minPrice
    }

    if (maxPrice) {
      filter.maxPrice = maxPrice
    } else {
      delete filter.maxPrice
    }

    setFilter(cloneDeep(filter))
  }, [
    maxPrice,
    minPrice,
    sortDirection,
    sortKey,
    deliveryType,
    searchText,
  ])

  return (
    <>
      <div className={styles['product-container']}>
        <div className={styles['product-hd']} ref={productHd}>
          <div className={styles['searchbar-box']}>
            <LeftOutline fontSize={px2rem(28)} color='#222' onClick={() => navigate(-1)} />
            <SearchBar className={styles['search-bar']} enableSearch={true} onSearch={onSearch} onScanSearch={handleScanSearch} />
          </div>
        </div>
        <div
          className={styles['product-bd']}
          style={{ height: `calc(100% - ${productHdHeight}px)` }}
        >
          <div className={styles['product-list']}>
            <div className={styles['product-toolbar']}>
              {/* 排序 */}
              <div
                className={styles['product-toolbar-sort']}
                onClick={toggleShowSortModal}
              >
                <img
                  className={styles['product-toolbar-sort-icon']}
                  src={iconSort}
                />
                <span className={styles['product-toolbar-sort-text']}>
                  {t('排序')}
                </span>
              </div>

              {/* 筛选 */}
              <div
                className={styles['product-toolbar-filter']}
                onClick={toggleShowFilterModal}
              >
                <img
                  className={styles['product-toolbar-filter-icon']}
                  src={iconFilter}
                />
                <span className={styles['product-toolbar-filter-text']}>
                  {t('筛选')}
                </span>
              </div>

              {/* 视图切换 */}
              {/* <div
                className={styles["product-toolbar-mode"]}
                onClick={handleChangeViewMode}
                >
                {listMode ? (
                  <img
                    className={styles["product-toolbar-mode-icon"]}
                    src={iconModeBig}
                  />
                ) : (
                  <img
                    className={styles["product-toolbar-mode-icon"]}
                    src={iconModeList}
                  />
                )}
                <span className={styles["product-toolbar-mode-text"]}>
                  {listMode ? "双列" : "列表"}
                </span>
                </div> */}
            </div>

            {/* 排序弹窗 */}
            {showSortModal && (
              <SortModal
                sortIndex={sortIndex}
                sortDirection={sortDirection}
                onToggleShowSortModal={toggleShowSortModal}
                onChangeSort={handleChangeSort}
              />
            )}

            {/* 价格过滤弹窗 */}
            {showFilterModal && (
              <FilterModal
                min={minPrice}
                max={maxPrice}
                deliveryType={deliveryType}
                onChangeFilter={handlePriceFilterChange}
                onToggleShowFilterModal={toggleShowFilterModal}
              />
            )}

            <div className={styles['product-list-container']}>
              <ProductList
                listMode={listMode}
                requestParams={filter}
                isFilterPage={false}
              />
            </div>
          </div>
        </div>
      </div>
      <Tabbar />
    </>
  )
}

export default CategorySearch
